import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Brain, 
  Building2, 
  Laptop, 
  Clock, 
  DollarSign,
  Search,
  Phone,
  ArrowRight,
  BookOpen,
  GraduationCap,
  Heart,
  Users,
  Shield,
  Sparkles,
  Activity,
  CheckCircle,
  HelpCircle
} from "lucide-react";
import { Header, Footer } from "../../components/Layout";

const TratamientoAdiccionesLandingPage = () => {
  // Treatment types with icons and descriptions
  const treatmentTypes = [
    {
      title: "Tratamiento Cognitivo-Conductual",
      description: "Entiende cómo tus pensamientos influyen en tus emociones y comportamientos. El TCC te ayuda a reemplazar patrones destructivos.",
      icon: Brain,
      path: "/tratamiento-cognitivo-conductual",
      readTime: "12 min",
      color: "from-violet-500 to-purple-600"
    },
    {
      title: "Tratamiento Ambulatorio",
      description: "Recuperación sin internación. Mantén tu vida laboral y familiar. Duración: 6-12 meses. Desde $350.000/mes.",
      icon: Clock,
      path: "/tratamiento-ambulatorio",
      readTime: "10 min",
      color: "from-blue-500 to-cyan-600"
    },
    {
      title: "Tratamiento Online",
      description: "Rehabilitación desde casa con acompañamiento profesional. Duración: 6-12 meses. Desde $200.000/mes.",
      icon: Laptop,
      path: "/tratamiento-online",
      readTime: "9 min",
      color: "from-emerald-500 to-teal-600"
    },
    {
      title: "Tratamiento Residencial",
      description: "Internación completa con estructura 24/7. Duración: 6-12 meses + seguimiento. Desde $350.000/mes.",
      icon: Building2,
      path: "/centro/residencial",
      readTime: "Ver centros",
      color: "from-orange-500 to-red-600"
    },
    {
      title: "Guía Completa de Tratamientos",
      description: "Compara tipos, costos, duraciones y elige el tratamiento adecuado para ti o tu familia.",
      icon: BookOpen,
      path: "/guia-tratamientos",
      readTime: "15 min",
      color: "from-pink-500 to-rose-600"
    },
    {
      title: "Centros de Rehabilitación Gratis",
      description: "Accede a tratamiento gratuito vía SENDA y FONASA. Pasos detallados para conseguir cupo.",
      icon: DollarSign,
      path: "/centros-rehabilitacion-gratis",
      readTime: "10 min",
      color: "from-green-500 to-emerald-600"
    }
  ];

  // Link to addiction types page
  const addictionTypesPreview = [
    { name: "Alcoholismo", icon: "🍺", path: "/adicciones/alcohol" },
    { name: "Cocaína y Pasta Base", icon: "⚡", path: "/adicciones/cocaina" },
    { name: "Marihuana", icon: "🌿", path: "/adicciones/marihuana" },
    { name: "Medicamentos", icon: "💊", path: "/adicciones/medicamentos" },
    { name: "Ludopatía", icon: "🎰", path: "/adicciones/ludopatia" },
    { name: "Adicciones Conductuales", icon: "📱", path: "/adicciones/conductuales" }
  ];

  // Self-assessment tests
  const tests = [
    { name: "Test AUDIT", description: "Evalúa tu consumo de alcohol", path: "/herramientas/audit", icon: "🍷" },
    { name: "Test DAST-10", description: "Detecta dependencia a drogas", path: "/herramientas/dast", icon: "💉" },
    { name: "Test PGSI", description: "Mide riesgo de ludopatía", path: "/herramientas/pgsi", icon: "🎲" },
    { name: "Test URICA", description: "Evalúa motivación al cambio", path: "/herramientas/urica", icon: "🎯" }
  ];

  // FAQ for SEO and AI
  const faqs = [
    {
      question: "¿Cuánto dura un tratamiento de adicciones?",
      answer: "La duración promedio es de 6 a 12 meses de tratamiento activo, seguido de 6 a 12 meses adicionales de seguimiento y prevención de recaídas. El tratamiento residencial típicamente dura 30-90 días de internación intensiva, continuando luego con fase ambulatoria. La duración exacta depende del tipo de adicción, severidad y respuesta individual al tratamiento."
    },
    {
      question: "¿Cuánto cuesta un tratamiento de rehabilitación en Chile?",
      answer: "Los costos varían según la modalidad. El tratamiento residencial va desde $350.000 hasta $3.000.000 CLP mensuales dependiendo del centro y servicios incluidos. El tratamiento ambulatorio cuesta entre $350.000 y $600.000 CLP mensuales. El tratamiento online parte desde $200.000 CLP mensuales. También existen opciones gratuitas a través de SENDA y FONASA para quienes califican."
    },
    {
      question: "¿Qué tipo de tratamiento es mejor para la adicción?",
      answer: "No existe un tratamiento único que funcione para todos. La elección depende del tipo de sustancia, severidad de la adicción, condiciones de salud coexistentes, y circunstancias personales. Para adicciones severas (como pasta base), se recomienda tratamiento residencial. Para adicciones moderadas con buen sistema de apoyo, el tratamiento ambulatorio puede ser efectivo."
    },
    {
      question: "¿Es posible recuperarse de una adicción?",
      answer: "Sí, la recuperación es absolutamente posible. Estudios muestran que con tratamiento adecuado, hasta el 70% de las personas logran mantener la sobriedad a largo plazo. La adicción es una enfermedad crónica tratable, no una sentencia de vida. Factores como motivación personal, apoyo familiar y continuidad del tratamiento son clave para el éxito."
    },
    {
      question: "¿Cómo sé si necesito tratamiento profesional?",
      answer: "Algunas señales incluyen: pérdida de control sobre el consumo, necesidad de cantidades mayores para el mismo efecto, síntomas de abstinencia al dejar, abandono de actividades importantes, y continuar consumiendo a pesar de consecuencias negativas. Si reconoces 3 o más de estas señales, es recomendable buscar evaluación profesional. Puedes comenzar con nuestros tests de autoevaluación."
    },
    {
      question: "¿Qué incluye un programa de tratamiento residencial?",
      answer: "Un programa residencial completo típicamente incluye: desintoxicación médica supervisada, terapia individual y grupal, tratamiento de condiciones coexistentes (depresión, ansiedad), talleres ocupacionales y recreativos, educación sobre la enfermedad, desarrollo de habilidades para prevenir recaídas, terapia familiar, y un plan de seguimiento post-alta."
    }
  ];

  // Schema markup for SEO
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "Tratamiento de Adicciones en Chile - Guía Completa",
    "description": "Información completa sobre tratamientos de adicciones: tipos, costos, duración y cómo elegir el programa adecuado en Chile.",
    "medicalAudience": {
      "@type": "MedicalAudience",
      "audienceType": "Patient"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamiento de Adicciones", "item": "https://sinadicciones.org/tratamiento-adicciones" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tratamiento de Adicciones en Chile | Tipos, Costos y Guías | Sin Adicciones</title>
        <meta name="description" content="Guía completa sobre tratamientos de adicciones en Chile. Conoce los tipos de tratamiento (residencial, ambulatorio, online), costos, duración y cómo elegir el programa adecuado." />
        <meta property="og:title" content="Tratamiento de Adicciones en Chile | Guía Completa" />
        <meta property="og:description" content="Todo sobre tratamientos de adicciones: tipos, costos, duración. Encuentra el programa ideal para tu recuperación." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento-adicciones" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento-adicciones" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      {/* Header unificado */}
      <Header />

      {/* Spacer para el header fixed */}
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Tratamiento de Adicciones</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <GraduationCap className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Guía de Tratamientos</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Tratamiento de{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                Adicciones en Chile
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Conoce los diferentes tipos de tratamiento, entiende sus diferencias y encuentra el camino hacia la recuperación. 
              Información confiable para pacientes y familias.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Centros
              </Link>
              <Link 
                to="/herramientas"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Hacer un Test
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Types Section */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tipos de Tratamiento
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Cada persona es diferente. Conoce las opciones disponibles y elige el tratamiento que mejor se adapte a tu situación.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatmentTypes.map((treatment, index) => (
              <Link 
                key={index}
                to={treatment.path}
                className="group relative overflow-hidden rounded-2xl bg-slate-800/50 border border-slate-700/50 hover:border-emerald-500/50 transition-all duration-300"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${treatment.color} opacity-0 group-hover:opacity-10 transition-opacity`} />
                
                <div className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${treatment.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <treatment.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                    {treatment.title}
                  </h3>
                  
                  <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                    {treatment.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{treatment.readTime}</span>
                    <ArrowRight className="w-5 h-5 text-emerald-400 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Link to Addiction Types Hub */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-br from-purple-900/30 to-slate-800/50 rounded-3xl p-8 md:p-12 border border-purple-500/20">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  Tipos de Adicción
                </h2>
                <p className="text-slate-400">
                  Conoce las características específicas de cada tipo de adicción.
                </p>
              </div>
              <Link 
                to="/adicciones"
                className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap"
              >
                Ver todas las adicciones
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {addictionTypesPreview.map((addiction, index) => (
                <Link 
                  key={index}
                  to={addiction.path}
                  className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-purple-500/30 transition-all group"
                >
                  <span className="text-3xl">{addiction.icon}</span>
                  <span className="text-white font-medium group-hover:text-purple-400 transition-colors">
                    {addiction.name}
                  </span>
                  <ArrowRight className="w-5 h-5 text-slate-500 ml-auto group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Self-Assessment Tests */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tests de Autoevaluación
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Herramientas científicas para evaluar tu situación de forma confidencial y gratuita.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tests.map((test, index) => (
              <Link 
                key={index}
                to={test.path}
                className="group p-6 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-emerald-500/50 transition-all text-center"
              >
                <span className="text-4xl block mb-3">{test.icon}</span>
                <h3 className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors mb-1">
                  {test.name}
                </h3>
                <p className="text-slate-400 text-sm">{test.description}</p>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link 
              to="/herramientas"
              className="text-emerald-400 hover:text-emerald-300 font-medium inline-flex items-center gap-1"
            >
              Ver todas las herramientas
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Preguntas Frecuentes sobre Tratamiento
            </h2>
            <p className="text-slate-400">
              Respuestas a las dudas más comunes sobre el tratamiento de adicciones.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  <ChevronRight className="w-5 h-5 text-emerald-400 transform group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Help CTA Section */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-8 h-8 text-emerald-400" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  ¿Necesitas ayuda ahora?
                </h2>
              </div>
              
              <p className="text-slate-300 mb-8 max-w-2xl">
                Si tú o un ser querido está pasando por una situación difícil, no estás solo. 
                Hay profesionales disponibles para ayudarte de forma confidencial.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <a 
                  href="tel:1412"
                  className="flex items-center gap-4 p-4 bg-slate-800/80 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">1412</p>
                    <p className="text-slate-400 text-sm">Fono Drogas - 24/7 Gratis</p>
                  </div>
                </a>
                
                <Link 
                  to="/asesoria"
                  className="flex items-center gap-4 p-4 bg-slate-800/80 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-white">Asesoría Experta</p>
                    <p className="text-slate-400 text-sm">Te orientamos gratis</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Search Centers */}
      <section className="py-16 bg-gradient-to-t from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Shield className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Encuentra el Centro Ideal para Ti
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">
            Más de 138 centros de rehabilitación verificados en todo Chile. 
            Filtra por ubicación, modalidad, precio y más.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/centros-de-rehabilitacion-drogas-chile"
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2 text-lg"
            >
              <Search className="w-6 h-6" />
              Buscar Centros
            </Link>
            <Link 
              to="/comparar"
              className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
            >
              Comparar Centros
            </Link>
          </div>
          
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              138 Centros
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              47 Profesionales
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              352 Talleres
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              Ayuda 24/7
            </span>
          </div>
        </div>
      </section>

      {/* Footer unificado */}
      <Footer />
    </div>
  );
};

export default TratamientoAdiccionesLandingPage;
